
import { Sparkles, Loader2 } from 'lucide-react';

interface DocumentAnalysisStatusProps {
  isAnalyzing?: boolean;
  relevanceScore?: number;
  onViewAssessment?: () => void;
}

const getRelevanceConfig = (score: number) => {
  if (score >= 80) {
    return {
      color: '#22c55e', // green-500
      bgColor: '#dcfce7', // green-100
      textColor: 'text-green-700',
      message: 'Contents relevant and demonstrate compliance'
    };
  } else if (score >= 60) {
    return {
      color: '#eab308', // yellow-500
      bgColor: '#fefce8', // yellow-50
      textColor: 'text-yellow-700',
      message: 'Contents may have gaps, needs manual review'
    };
  } else {
    return {
      color: '#ef4444', // red-500
      bgColor: '#fef2f2', // red-50
      textColor: 'text-red-700',
      message: 'Contents may not be relevant or sufficient to demonstrate compliance'
    };
  }
};

const DonutChart = ({ score, size = 40 }: { score: number; size?: number }) => {
  const config = getRelevanceConfig(score);
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="3"
          fill="transparent"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={config.color}
          strokeWidth="3"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-300 ease-in-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-xs font-medium ${config.textColor}`}>
          {score}%
        </span>
      </div>
    </div>
  );
};

export const DocumentAnalysisStatus = ({ 
  isAnalyzing, 
  relevanceScore, 
  onViewAssessment 
}: DocumentAnalysisStatusProps) => {
  if (isAnalyzing) {
    return (
      <div className="flex items-center text-primary text-xs gap-1 mt-1">
        <Sparkles className="h-3 w-3" />
        <span>AI analysis in progress</span>
        <Loader2 className="h-3 w-3 ml-1 animate-spin" />
      </div>
    );
  }

  if (relevanceScore !== undefined) {
    const config = getRelevanceConfig(relevanceScore);
    
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-2 text-primary cursor-pointer hover:text-primary/80"
            onClick={onViewAssessment}
            title="Click to view AI assessment"
          >
            <Sparkles className="h-3 w-3" />
            <span className="text-xs">AI analysis: {relevanceScore}% relevant</span>
          </div>
          <div className="flex items-center gap-0.5">
            <Sparkles className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />
            <Sparkles className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />
            <Sparkles className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <DonutChart score={relevanceScore} size={32} />
          <div className={`text-xs px-2 py-1 rounded-md ${config.textColor}`} style={{ backgroundColor: config.bgColor }}>
            {config.message}
          </div>
        </div>
      </div>
    );
  }

  return null;
};
